import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sparkles } from "lucide-react";
import { TID, NAV_LINKS } from "@/constants/site";
import LogoMark from "@/components/LogoMark";

const Navbar = ({ onAida }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "glass-nav py-3" : "py-5 bg-transparent"}`}
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 flex items-center justify-between gap-6">
        <button data-testid={TID.nav.logo} onClick={() => go("home")} className="flex items-center gap-2.5 group shrink-0">
          <LogoMark className="h-8 w-8 group-hover:rotate-45 transition-transform duration-500" />
          <span className="font-display text-[22px] font-extrabold tracking-tight text-[#0B2429] leading-none">
            aida<span className="text-[#43B0CE] group-hover:text-[#5FC4DB] transition-colors">nova</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={TID.nav.link(l.id)}
              onClick={() => go(l.id)}
              className="text-[13.5px] font-medium text-[#3C5156] hover:text-[#43B0CE] transition-colors relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#43B0CE] group-hover:w-full transition-all duration-300" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid={TID.nav.aidaBtn}
            onClick={onAida}
            className="hidden sm:inline-flex items-center gap-2 rounded-[10px] bg-[#43B0CE] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(67,176,206,0.28)] hover:bg-[#5FC4DB] transition-all"
          >
            <Sparkles className="h-4 w-4" /> Ask Aida
          </button>
          <button
            data-testid={TID.nav.mobileToggle}
            onClick={() => setOpen((v) => !v)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-black/10 bg-white/80 text-[#0B2429] hover:border-[#43B0CE]/50 hover:text-[#43B0CE] transition-all"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden glass-nav mt-3 rounded-2xl mx-4 sm:mx-6 lg:mx-10 mb-4"
          >
            <div className="px-6 py-4 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="text-left text-sm font-medium text-[#3C5156] hover:text-[#43B0CE] transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <button onClick={onAida} className="text-left text-sm font-semibold text-[#43B0CE]">
                Ask Aida →
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
